import { lazy, LocationProvider, Router, Route, ErrorBoundary } from 'preact-iso';
import { AppShell } from './infra/AppShell.js';
import { CATALOG, entryHref } from './catalog.js';
import type { CatalogEntry } from './catalog.js';

/**
 * Top-level router.
 *
 * Live-auth pages (/login, /register, /admin/*, etc.) are listed
 * explicitly below — they don't live in the catalog. Every
 * catalog page (atoms/forms/flows) is wired automatically by
 * walking `CATALOG` and resolving each entry to its page module.
 *
 * The page-module map is built once at module-load via
 * `import.meta.glob` — Vite statically analyses the glob and emits
 * a lazy-import factory per match, so we still get a separate
 * chunk per page (no bundle bloat). Adding a new catalog page
 * = (a) drop a CatalogEntry into `catalog.ts`, (b) create the
 * `<Tier>/<PascalId>Page.tsx` file. No edits here.
 */

// Eagerly enumerate every page file matching the convention. Vite
// expands this at build time into a record of lazy import factories.
// Result shape: { './pages/atoms/AuthStatusBadgePage.tsx': () => Promise<Module> }
const PAGE_MODULES = import.meta.glob<{ default: any }>('./pages/{atoms,forms,flows}/*Page.tsx');

/** Convert `kebab-case` to `PascalCase`. */
function pascalize(id: string): string {
    return id
        .split('-')
        .map((s) => (s ? s[0].toUpperCase() + s.slice(1) : ''))
        .join('');
}

/** Resolve a catalog entry to its lazy-import factory, or null if
 *  the corresponding page file doesn't exist. */
function pageImporterFor(entry: CatalogEntry): (() => Promise<{ default: any }>) | null {
    const key = `./pages/${entry.tier}/${pascalize(entry.id)}Page.tsx`;
    return PAGE_MODULES[key] ?? null;
}

/** All catalog entries paired with their resolved lazy components.
 *  Missing pages are dropped with a console warning — the catalog
 *  is the source of truth for the index page, so a missing file
 *  shouldn't crash the whole app. */
const CATALOG_ROUTES = CATALOG
    .map((entry) => {
        const importer = pageImporterFor(entry);
        if (!importer) {
            // eslint-disable-next-line no-console
            console.warn(`catalog: no page file for ${entry.tier}/${entry.id} — skipping route`);
            return null;
        }
        return { entry, Component: lazy(() => importer().then((m) => m.default)) };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);

const IndexPage = lazy(() => import('./pages/IndexPage.js').then((m) => m.IndexPage));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage.js').then((m) => m.NotFoundPage));

/* ── Live-auth pages — not catalog-driven ─────────────────────── */
const LoginPage = lazy(() => import('./pages/LoginPage.js').then((m) => m.LoginPage));
const RegisterPage = lazy(() => import('./pages/RegisterPage.js').then((m) => m.RegisterPage));
const ForgotPage = lazy(() => import('./pages/ForgotPage.js').then((m) => m.ForgotPage));
const ProfilePage = lazy(() => import('./pages/ProfilePage.js').then((m) => m.ProfilePage));
const AdminUsersPage = lazy(() => import('./pages/AdminUsersPage.js').then((m) => m.AdminUsersPage));
const AdminUserEditPage = lazy(() => import('./pages/AdminUserEditPage.js').then((m) => m.AdminUserEditPage));
const AdminApplicationsPage = lazy(() => import('./pages/AdminApplicationsPage.js').then((m) => m.AdminApplicationsPage));
const AdminApplicationEditPage = lazy(() => import('./pages/AdminApplicationEditPage.js').then((m) => m.AdminApplicationEditPage));
const AdminOrganizationsPage = lazy(() => import('./pages/AdminOrganizationsPage.js').then((m) => m.AdminOrganizationsPage));
const AdminOrganizationEditPage = lazy(() => import('./pages/AdminOrganizationEditPage.js').then((m) => m.AdminOrganizationEditPage));
const AdminAuditLogPage = lazy(() => import('./pages/AdminAuditLogPage.js').then((m) => m.AdminAuditLogPage));
const MagicLinkVerifyPage = lazy(() => import('./pages/MagicLinkVerifyPage.js').then((m) => m.MagicLinkVerifyPage));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage.js').then((m) => m.VerifyEmailPage));

export function App() {
    return (
        <LocationProvider>
            <AppShell>
                <ErrorBoundary>
                    <Router>
                        <Route path="/" component={IndexPage} />

                        {/* Live-auth — real, gated auth flows */}
                        <Route path="/login" component={LoginPage} />
                        <Route path="/register" component={RegisterPage} />
                        <Route path="/auth/forgot" component={ForgotPage} />
                        <Route path="/profile" component={ProfilePage} />
                        <Route path="/admin/users" component={AdminUsersPage} />
                        <Route path="/admin/users/:id" component={AdminUserEditPage} />
                        <Route path="/admin/applications" component={AdminApplicationsPage} />
                        <Route path="/admin/applications/:id" component={AdminApplicationEditPage} />
                        <Route path="/admin/organizations" component={AdminOrganizationsPage} />
                        <Route path="/admin/organizations/:id" component={AdminOrganizationEditPage} />
                        <Route path="/admin/audit-log" component={AdminAuditLogPage} />
                        <Route path="/auth/magic-link/verify" component={MagicLinkVerifyPage} />
                        <Route path="/auth/verify-email" component={VerifyEmailPage} />
                        {/* /auth/reset is the URL we ship in password-reset emails;
                            /auth/forgot is the in-app entry point. Same component. */}
                        <Route path="/auth/reset" component={ForgotPage} />

                        {/* Catalog pages — derived from `CATALOG` at module-load. */}
                        {CATALOG_ROUTES.map(({ entry, Component }) => (
                            <Route key={entry.id} path={entryHref(entry)} component={Component} />
                        ))}

                        <Route default component={NotFoundPage} />
                    </Router>
                </ErrorBoundary>
            </AppShell>
        </LocationProvider>
    );
}
